import * as Sentry from "@sentry/react";

import { UserTagCategoryOverview, UserTagOverview } from "api/generated/model";
import { TagsData } from "components/tag/types";

import { TagsCascader } from "./tagsCascader";
import { TagCascaderOption } from "./types";

interface TagsSelectFilterProps {
    placeholder?: string;
    uniqueTagIds: string[];
    tags: UserTagOverview[] | TagsData["tags"];
    tagCategories: UserTagCategoryOverview[] | TagsData["tagCategories"];
    setFilterValue: (value: string[]) => void;
    value: string[] | null;
}
export function TagsSelectFilter({
    placeholder,
    uniqueTagIds,
    tags,
    tagCategories,
    setFilterValue,
    value,
}: TagsSelectFilterProps) {
    const options = buildTagCascaderOptions(uniqueTagIds, tags, tagCategories);

    // Transform the value from tag IDs to cascader format
    const cascaderValue = transformTagIdsToCascaderValue(value, tags, options);

    return (
        <TagsCascaderFilter
            placeholder={placeholder}
            options={options}
            tags={tags}
            setFilterValue={setFilterValue}
            value={cascaderValue}
        />
    );
}

export function buildTagCascaderOptions(
    uniqueTagIds: string[],
    tags: UserTagOverview[] | TagsData["tags"],
    tagCategories: UserTagCategoryOverview[] | TagsData["tagCategories"]
): TagCascaderOption[] {
    const tagOptions = uniqueTagIds.reduce((acc, tagId) => {
        const tag = tags.find((tRes) => tRes.id === tagId);
        if (!tag) {
            const err = new Error(`Tag not found for tag ID ${tagId}`);
            console.error(err);
            Sentry.captureException(err);
            return acc;
        }

        const tagCategory = tagCategories.find(
            (category) => category.id === tag.tagCategoryId
        );
        if (!tagCategory) {
            const err = new Error(
                `Tag category not found for tag category ID ${tag.tagCategoryId}`
            );
            console.error(err);
            Sentry.captureException(err);
            return acc;
        }

        // Find existing category option or create new one
        let categoryOption = acc.find((opt) => opt.value === tagCategory.id);
        if (!categoryOption) {
            categoryOption = {
                value: tagCategory.id,
                label: tagCategory.name,
                children: [],
            };
            acc.push(categoryOption);
        }

        // Add tag as child if not already present
        if (!categoryOption.children.find((child) => child.value === tag.id)) {
            categoryOption.children.push({
                value: tag.id,
                label: tag.name,
            });
        }
        return acc;
    }, [] as TagCascaderOption[]);
    const collator = new Intl.Collator();
    const sortedTagOptions = tagOptions
        .map((category) => ({
            ...category,
            children: category.children.sort((a, b) =>
                collator.compare(a.label, b.label)
            ),
        }))
        .sort((a, b) => collator.compare(a.label, b.label));

    return sortedTagOptions;
}

function transformTagIdsToCascaderValue(
    tagIds: string[] | null,
    tags: UserTagOverview[] | TagsData["tags"],
    options: TagCascaderOption[]
): string[][] | null {
    if (!tagIds) {
        return null;
    }
    const tagsWithCategories = tagIds
        .map((tagId) => {
            const tag = tags.find((t) => t.id === tagId);
            if (!tag) {
                return [];
            }

            // Find the category option that contains this tag
            const categoryOption = options.find(
                (opt) => opt.value === tag.tagCategoryId
            );
            if (!categoryOption) {
                return [];
            }

            // Check if the tag exists as a child of this category
            const tagChild = categoryOption.children.find(
                (child) => child.value === tagId
            );
            if (tagChild) {
                // Return [categoryId, tagId] for specific tag selection
                return [tag.tagCategoryId, tagId];
            } else {
                // If tag not found as child, return just [categoryId] for category selection
                return [tag.tagCategoryId];
            }
        })
        .filter((value) => value.length > 0);
    return tagsWithCategories;
}

interface TagsCascaderFilterProps {
    placeholder?: string;
    options: TagCascaderOption[];
    tags: UserTagOverview[] | TagsData["tags"];
    setFilterValue: (value: string[]) => void;
    value?: string[][] | null;
}

function TagsCascaderFilter({
    placeholder,
    options,
    tags,
    setFilterValue,
    value,
}: TagsCascaderFilterProps) {
    const handleChange = (value: (string | number)[][]) => {
        const tagIds: string[] = value.reduce(
            (acc: string[], value: (string | number)[]) => {
                if (value.length === 1) {
                    const allTagsInCategory = tags.filter(
                        (tag) => tag.tagCategoryId === (value[0] as string)
                    );
                    return [...acc, ...allTagsInCategory.map((tag) => tag.id)];
                }
                return [...acc, value[1] as string];
            },
            []
        );
        setFilterValue(tagIds);
    };

    return (
        <TagsCascader
            placeholder={placeholder}
            options={options}
            onChange={handleChange}
            value={value}
        />
    );
}
